import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Logo, useToast } from '@vd/ui';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { useCurrentCompany } from '../features/auth/AuthProvider';
import { useTheme } from '../app/theme';
import { get } from '../lib/apiClient';

/**
 * Post-checkout thank-you page. Stripe redirects here after a successful
 * Checkout Session, with `?session_id=...` in the URL. We don't validate
 * the session id (the backend webhook is the source of truth); the page
 * just polls /billing/subscription until the plan flips from 'free' to
 * one of the paid tiers, then shows what the user unlocked.
 *
 * Polling is bounded: after ~30 seconds of no plan change we surface a
 * "still updating" state with a manual continue link, so a delayed or
 * dropped webhook doesn't trap the user on a spinner forever.
 */

interface SubResponse {
  plan: 'free' | 'personal' | 'team' | 'business' | 'enterprise';
  planInterval: 'monthly' | 'annual' | null;
  seatsPurchased: number;
}

const PLAN_LABELS: Record<SubResponse['plan'], string> = {
  free: 'Free',
  personal: 'Personal',
  team: 'Team',
  business: 'Business',
  enterprise: 'Enterprise',
};

const PLAN_FEATURES: Record<'personal' | 'team' | 'business', string[]> = {
  personal: [
    'Unlimited tickets each month',
    'Full annotation, redaction, and capture history',
    'AI auto-titles on every capture',
    'Cancel any time from the billing portal',
  ],
  team: [
    'Unlimited tickets each month',
    'Add teammates as members or viewers (no minimum, no maximum)',
    'Full annotation, redaction, and capture history for the whole team',
    'AI auto-titles on every capture',
    'Real-time collaboration and custom branding (rolling out soon)',
  ],
  business: [
    'Everything in Team, from three seats',
    'Twice the recording and narration minutes per seat',
    'A year of walkthrough video',
    'Widget Starter included',
    'Audit log export with dates and details',
  ],
};

async function fetchPlan(companyId: string): Promise<SubResponse | null> {
  try {
    return await get<SubResponse>(`/v1/billing/subscription?companyId=${encodeURIComponent(companyId)}`);
  } catch {
    return null;
  }
}

export function BillingSuccessPage() {
  const company = useCurrentCompany();
  const navigate = useNavigate();
  const toast = useToast();
  const { theme } = useTheme();

  const [plan, setPlan] = useState<SubResponse | null>(null);
  const [pollingTimedOut, setPollingTimedOut] = useState(false);

  // Poll /billing/subscription until the plan flips off 'free', or until
  // the timeout cap. The Stripe webhook usually arrives within a couple
  // seconds, but we give it a generous window before surfacing the
  // "still updating" copy so a healthy network blip doesn't show it.
  useEffect(() => {
    if (!company?.id) return;
    let cancelled = false;
    const start = Date.now();
    const TIMEOUT_MS = 30_000;
    const POLL_MS = 2_000;

    const tick = async () => {
      if (cancelled) return;
      const result = await fetchPlan(company.id);
      if (cancelled) return;
      if (result) setPlan(result);
      const flipped = result && result.plan !== 'free';
      if (flipped) return;
      if (Date.now() - start >= TIMEOUT_MS) {
        setPollingTimedOut(true);
        return;
      }
      setTimeout(tick, POLL_MS);
    };
    void tick();
    return () => { cancelled = true; };
  }, [company?.id]);

  const onContinue = () => {
    navigate('/inbox/assigned', { replace: true });
  };

  const onCopyReceipt = () => {
    const url = new URL(window.location.href);
    void navigator.clipboard.writeText(url.toString()).then(() => {
      toast.push({ variant: 'info', title: 'Link copied' });
    });
  };

  const isPaid = plan && (plan.plan === 'personal' || plan.plan === 'team' || plan.plan === 'business');
  const showWaiting = !pollingTimedOut && !isPaid;
  const features = isPaid && plan && plan.plan !== 'enterprise' && plan.plan !== 'free'
    ? PLAN_FEATURES[plan.plan]
    : [];
  const logoSrc = theme === 'dark' ? '/toado%20logo%20white.svg' : '/toado%20logo.svg';

  return (
    <div style={shellStyle}>
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
          <img src={logoSrc} alt="Toado" style={{ height: 32 }} />
        </div>

        {showWaiting && (
          <>
            <style>{`@keyframes vd-bs-spin { to { transform: rotate(360deg); } }`}</style>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12, color: 'var(--text-secondary)' }}>
              <Loader2 size={36} style={{ animation: 'vd-bs-spin 1.2s linear infinite' }} />
            </div>
            <h1 style={titleStyle}>Payment received</h1>
            <p style={subStyle}>
              We are activating your plan now. This usually takes a couple of seconds.
            </p>
          </>
        )}

        {isPaid && plan && (
          <>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12, color: 'var(--success)' }}>
              <CheckCircle2 size={40} />
            </div>
            <h1 style={titleStyle}>You are on the {PLAN_LABELS[plan.plan]} plan</h1>
            <p style={subStyle}>Thanks for upgrading. Here is what you just unlocked.</p>
            <ul style={featuresStyle}>
              {features.map((f) => (
                <li key={f} style={featureItemStyle}>
                  <CheckCircle2 size={14} style={{ color: 'var(--success)', flexShrink: 0, marginTop: 3 }} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            {(plan.plan === 'team' || plan.plan === 'business') && plan.seatsPurchased > 0 && (
              <p style={{ ...subStyle, marginTop: 16 }}>
                You currently have {plan.seatsPurchased} seat{plan.seatsPurchased === 1 ? '' : 's'}. Add teammates from
                {' '}<strong>Settings → Members</strong>.
              </p>
            )}
          </>
        )}

        {pollingTimedOut && !isPaid && (
          <>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12, color: 'var(--warning)' }}>
              <CheckCircle2 size={40} />
            </div>
            <h1 style={titleStyle}>Payment received</h1>
            <p style={subStyle}>
              Your charge went through, but we are still finishing the activation in the background. You can keep using Toado
              while it catches up. If your plan still shows as Free in a few minutes, contact support and we will sort it out.
            </p>
          </>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 24 }}>
          <Button variant="primary" onClick={onContinue}>Continue to your dashboard</Button>
          <Button variant="ghost" onClick={onCopyReceipt}>Copy link</Button>
        </div>
      </div>
    </div>
  );
}

// Inline styles keep this page self-contained and avoid module-CSS churn.
// The aesthetic mirrors AuthLayout so the centered-card pattern is
// consistent across pre-auth and post-checkout one-off screens.
const shellStyle: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
  background: 'var(--bg)',
};

const cardStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 480,
  padding: 32,
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 16,
  textAlign: 'center',
  fontFamily: 'Inter, sans-serif',
  color: 'var(--text)',
};

const titleStyle: React.CSSProperties = {
  fontSize: 22,
  fontWeight: 600,
  margin: '0 0 8px',
};

const subStyle: React.CSSProperties = {
  fontSize: 14,
  color: 'var(--text-secondary)',
  lineHeight: 1.5,
  margin: 0,
};

const featuresStyle: React.CSSProperties = {
  textAlign: 'left',
  margin: '20px auto 0',
  padding: 0,
  listStyle: 'none',
  maxWidth: 360,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};

const featureItemStyle: React.CSSProperties = {
  display: 'flex',
  gap: 10,
  alignItems: 'flex-start',
  fontSize: 13,
  color: 'var(--text-secondary)',
  lineHeight: 1.5,
};
